import type { PluginOption } from 'vite';
import { send } from 'vite';
import { CliError, CliErrorCode } from '@wix/cli-error';
import type { WixConfig } from '@wix/cli-app-definitions';
import { getMonitoringLoaderScript } from '@wix/monitoring-buildtime';
import {
  virtualDashboardHTMLAssets,
  virtualModuleIdForScriptAsset,
} from '../../dev-center/components/dashboard/config.js';
import type { ProjectModel } from '../../model.js';
import { ConfigType } from '../vite-config-options.js';
import type { ViteConfigOptions } from '../vite-config-options.js';
import { getViteServerUrl } from '../vite-server-url.js';
import {
  virtualCustomElementPanelHTMLAssets,
  virtualModuleIdForCustomElementPanelScriptAsset,
} from '../../dev-center/components/site/config.js';

type InlineHtmlOptions = {
  initialModel: ProjectModel;
} & ViteConfigOptions;

export function inlineHtml(options: InlineHtmlOptions): PluginOption {
  const { initialModel, type } = options;

  let virtualHTMLAssets = virtualDashboardHTMLAssets(initialModel);
  let virtualCustomElementPanelsAssets =
    virtualCustomElementPanelHTMLAssets(initialModel);

  if (type === ConfigType.dev) {
    // I'm listening to all events, not only to `add` and `unlink`.
    // Because if user added a component config with an error, then after the fix only `change` will be fired.
    // In this case I still need to update the entries.
    options.projectModelWatcher.onChange((model) => {
      virtualHTMLAssets = virtualDashboardHTMLAssets(model);
      virtualCustomElementPanelsAssets =
        virtualCustomElementPanelHTMLAssets(model);
    });
  }

  return {
    name: 'inline-html',

    resolveId(id) {
      if (
        virtualHTMLAssets.has(id) ||
        virtualCustomElementPanelsAssets.has(id)
      ) {
        return id;
      }

      return null;
    },

    load(id) {
      const dashboardAsset = virtualHTMLAssets.get(id);
      const customElementPanelAsset = virtualCustomElementPanelsAssets.get(id);
      if (!dashboardAsset && !customElementPanelAsset) {
        return null;
      }

      if (dashboardAsset) {
        const virtualScriptAssetPath = virtualModuleIdForScriptAsset(
          dashboardAsset.id
        );

        return htmlForDashboardComponent({
          componentId: dashboardAsset.id,
          componentPath: virtualScriptAssetPath,
          componentType: dashboardAsset.type,
          appId: initialModel.config.appId,
          type: options.type,
          monitoring: initialModel.config.monitoring,
        });
      }

      if (customElementPanelAsset) {
        const virtualScriptAssetPath =
          virtualModuleIdForCustomElementPanelScriptAsset(
            customElementPanelAsset.id
          );

        return htmlForCustomElementPanel({
          componentId: customElementPanelAsset.id,
          componentPath: virtualScriptAssetPath,
          componentType: customElementPanelAsset.type,
          appId: initialModel.config.appId,
          type: options.type,
          monitoring: initialModel.config.monitoring,
        });
      }
    },

    /* Vite's built-in dev-server will also crawl the root dir
       and return 404 when a path points to a non-existent `index.html` file:
       https://github.com/vitejs/vite/blob/8ef733368fd6618a252e44616f7577f593fd4fbc/packages/vite/src/node/server/middlewares/indexHtml.ts#L255-L282

       Here we add our own middleware that'll handle our non-existent `index.html` files.
       This is necessary for Vite's `dev` to work. */
    async configureServer(server) {
      server.middlewares.use(async (req, res, next) => {
        if (!req.url) {
          return next();
        }

        const serverUrl = getViteServerUrl(server);

        let url: URL;

        try {
          url = new URL(req.url, serverUrl);
        } catch (e) {
          throw new CliError({
            code: CliErrorCode.TypeErrorInvalidUrl(),
            info: {
              url: req.url,
              resolvedUrl: serverUrl,
            },
            cause: e,
          });
        }

        // url.pathname has a leading slash, and our assetId doesn't
        const assetId = url.pathname.replace(/^\/+/, '');
        const dashboardAsset = virtualHTMLAssets.get(assetId);
        const customElementPanelAsset =
          virtualCustomElementPanelsAssets.get(assetId);
        if (!dashboardAsset && !customElementPanelAsset) {
          return next();
        }

        if (dashboardAsset) {
          const virtualScriptAssetPath = virtualModuleIdForScriptAsset(
            dashboardAsset.id
          );

          const transformedHtml = await server.transformIndexHtml(
            req.url,
            htmlForDashboardComponent({
              componentId: dashboardAsset.id,
              componentPath: virtualScriptAssetPath,
              componentType: dashboardAsset.type,
              appId: initialModel.config.appId,
              type: options.type,
              monitoring: initialModel.config.monitoring,
            }),
            req.originalUrl
          );

          return send(req, res, transformedHtml, 'html', {
            headers: server.config.server.headers,
          });
        }

        if (customElementPanelAsset) {
          const virtualScriptAssetPath =
            virtualModuleIdForCustomElementPanelScriptAsset(
              customElementPanelAsset.id
            );

          const transformedHtml = await server.transformIndexHtml(
            req.url,
            htmlForCustomElementPanel({
              componentId: customElementPanelAsset.id,
              componentPath: virtualScriptAssetPath,
              componentType: customElementPanelAsset.type,
              appId: initialModel.config.appId,
              type: options.type,
              monitoring: initialModel.config.monitoring,
            }),
            req.originalUrl
          );

          return send(req, res, transformedHtml, 'html', {
            headers: server.config.server.headers,
          });
        }
      });
    },
  };
}

const getExternalsTemplate = (type: ConfigType) =>
  type === ConfigType.dev
    ? `
<script crossorigin src="https://static.parastorage.com/unpkg/react@16.14.0/umd/react.development.js"></script>
<script crossorigin src="https://static.parastorage.com/unpkg/react-dom@16.14.0/umd/react-dom.development.js"></script>
`
    : `
<script crossorigin src="https://static.parastorage.com/unpkg/react@16.14.0/umd/react.production.min.js"></script>
<script crossorigin src="https://static.parastorage.com/unpkg/react-dom@16.14.0/umd/react-dom.production.min.js"></script>
`;

const getMonitoringTemplate = ({
  type,
  monitoring,
  context,
}: {
  type: ConfigType;
  monitoring: WixConfig['monitoring'];
  context: {
    appId: string;
    componentId: string;
    componentType: string;
  };
}) => {
  if (monitoring && 'sentry' in monitoring) {
    try {
      return getMonitoringLoaderScript({
        config: {
          type: 'SENTRY',
          sentryOptions: {
            dsn: monitoring.sentry.dsn,
          },
        },
        environment: ConfigType.dev === type ? 'development' : 'production',
        hostContext: {
          appId: context.appId,
          extensionType: context.componentType,
          extensionId: context.componentId,
        },
      }).htmlString;
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToGetMonitoringLoaderScript(monitoring.sentry),
        cause: e,
        info: { sentry: monitoring.sentry },
      });
    }
  }

  return '';
};

function htmlForDashboardComponent({
  componentId,
  componentType,
  componentPath,
  appId,
  type,
  monitoring,
}: {
  componentId: string;
  componentPath: string;
  componentType: string;
  appId: string;
  type: ConfigType;
  monitoring: WixConfig['monitoring'];
}) {
  return `<!DOCTYPE html>
  <html style="height: 100%;">
    <head>
      <meta charset="UTF-8" />
      <title>Dashboard Component</title>
    </head>
    <body style="margin: 0; height: 100%;">
      <div id="root" style="height: 100%;"></div>
      ${getExternalsTemplate(type)}
      ${getMonitoringTemplate({
        type,
        monitoring,
        context: {
          appId,
          componentId,
          componentType,
        },
      })}
      <script type="module">
        import { createElement } from 'react';
        import { render } from 'react-dom';
        import '${componentPath}';

        async function main() {
          const getComponent = await window['__module_federation__containers']['${appId}'].get('${componentId}');
          render(createElement(getComponent().default), document.getElementById('root'));
        }

        main();
      </script>
    </body>
  </html>`;
}

function htmlForCustomElementPanel({
  componentId,
  componentPath,
  componentType,
  appId,
  type,
  monitoring,
}: {
  componentId: string;
  componentPath: string;
  componentType: string;
  appId: string;
  type: ConfigType;
  monitoring: WixConfig['monitoring'];
}) {
  return `<!DOCTYPE html>
  <html>
    <head>
      <meta charset="UTF-8" />
      <title>Settings Panel</title>
    </head>
    <body style="margin: 0">
      <div id="root"></div>
      ${getExternalsTemplate(type)}
      ${getMonitoringTemplate({
        type,
        monitoring,
        context: {
          appId,
          componentId,
          componentType,
        },
      })}
      <script type="module">
        import { createElement } from 'react';
        import { render } from 'react-dom';
        import SettingsPanel from '${componentPath}';

        render(createElement(SettingsPanel), document.getElementById('root'));
      </script>
    </body>
  </html>`;
}
